import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Form } from '@edgebox/react-components';
import { InternalId } from '@edgebox/data-definition-kit';
import { AiProviderStatus } from '@edgebox/sync-core-data-definitions';
import type { ISyncCoreApi } from '@edgebox/sync-core-rest-client';
import { ClientAiProviderEntity } from '@edgebox/sync-core-rest-client';

export function AiProviderSelector({
  api,
  value,
  onChange,
  disabled,
  name,
}: {
  api: ISyncCoreApi;
  value: InternalId | undefined;
  onChange: (nextId: InternalId | undefined, nextEntity: ClientAiProviderEntity | undefined) => void;
  disabled?: boolean;
  name?: string;
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(undefined);
  const [providers, setProviders] = useState<ClientAiProviderEntity[]>([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(undefined);

    api.ai.providers
      .list(
        { page: 0, itemsPerPage: 200 },
        {
          status: [AiProviderStatus.Active, AiProviderStatus.Draft],
        }
      )
      .then((res) => {
        if (cancelled) return;
        setProviders(res.items ?? []);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [api]);

  const options = useMemo(() => {
    return providers
      .slice()
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      .map((p) => ({ id: p.id, name: p.name, status: p.status, entity: p }));
  }, [providers]);

  const selectedId = value ?? '';

  if (error) {
    return (
      <Alert variant="danger" className="mb-0">
        Failed to load providers: {error instanceof Error ? error.message : String(error)}
      </Alert>
    );
  }

  return (
    <Form.Select
      value={selectedId}
      disabled={disabled || loading}
      name={name}
      id={name}
      onChange={(e) => {
        const id = e.target.value;
        if (!id) {
          onChange(undefined, undefined);
          return;
        }

        const provider = options.find((o) => o.id === id)?.entity;
        if (!provider) {
          onChange(undefined, undefined);
          return;
        }

        onChange(provider.id, provider);
      }}
    >
      <option value="">Select a provider…</option>
      {options.map((p) => (
        <option key={p.id} value={p.id}>
          {p.name} ({p.status})
        </option>
      ))}
    </Form.Select>
  );
}
